import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { loadStripeTerminal } from '@stripe/terminal-js'
import {
  calculations,
  getFlexPackVoucherServiceFee,
  isCompFlexPack,
} from '@blocktickets/shared-utils'

import isEqual from 'lodash/isEqual'
import moment from 'moment'

import { useModal } from '../../ModalProvider/ModalProvider'

import AuthService from '../../../utilities/services/auth.service'

import {
  capitalizeString,
  formatCurrency,
  groupPurchasePricing,
  packagePricing,
  formatTicketGroups,
  getPaymentOption,
  inPersonPaymentMethods,
  getAmountDue,
  getProcessingFeeFromChargeAmount,
  truncateToTwoDecimals,
} from '../../../utilities/helpers'

import {
  createOrderAssignment,
  createSeasonPackageOrder,
  createFlexPackAssignment,
  createAccessPassAssignment,
  getBuyerTypes,
  getSystemDefaultBuyerTypes,
  getPaymentIntent,
  pastPurchasers,
  customerPaymentMethods,
  getConnectionToken,
  updatePaymentIntent,
  createOrderPaymentPlan,
  changeExistingPaymentMethod,
  getCustomFees,
  addSeasonPackageOrderSeats,
  createSupplementalCustomFeeOrder,
  evaluateDonationAssignment,
  cancelTerminalPaymentIntent,
  waitForOrderByPaymentIntentId,
  holdAssignmentSeats,
  refreshAssignmentSeatHold,
  releaseAssignmentSeatHold,
  cancelPendingCheckoutOrder,
} from '../../../utilities/api'
import {
  buyerTypeHasTicketAssignmentSubType,
  compParentKeysFromDefaults,
  isAssignmentCompLabelMethod,
  isAssignmentLabelSelectionValid,
  isComplimentaryBuyerType,
} from '../../../utilities/buyerTypeFilters'

import Modal from 'react-bootstrap/Modal'

import {
  Confirm,
  Delete,
  Error,
  MarkAsComplete,
  Success,
  TerminalPaymentWaiting,
} from '../../../components/ModalContent'
import {
  getPaymentIntentIdFromClientSecret,
  runTerminalPaymentFlow,
  TERMINAL_PAYMENT_STATUS_LABELS,
} from '../../../utilities/terminalPayment'
import { LoadingContainer } from '../../../components/LoadingContainer'
import { ViewProfile } from '../../../components/ModalContent/ViewProfile'
import { PrintTicketsProvider } from '../../../providers/PrintTicketsProvider/PrintTicketsProvider'
import ModalFormV2 from './ModalFormV2'
import { assignmentCustomFeeSeedIds } from './packageAssignmentCustomFees'
import { constants } from '@blocktickets/shared-utils'

const { PAYMENT_TYPE, PRODUCT_TYPE, ORDER_SOURCE } = constants

function resolveCustomFeeLineAmount(feeEntity, percentBase) {
  if (!feeEntity) return 0
  const attrs = feeEntity.attributes ?? feeEntity
  const raw = Number(attrs?.feeAmount ?? 0)
  if (!Number.isFinite(raw)) return 0
  if (attrs?.option === 'percent') {
    const base = Number(percentBase) || 0
    return (base * raw) / 100
  }
  return raw
}

const ASSIGN_CARD_LIKE_METHODS = new Set(['CARD', 'INVOICE', 'POS'])
const isAssignCardLikePaymentMethod = (method) =>
  ASSIGN_CARD_LIKE_METHODS.has(String(method || '').toUpperCase())

const hasStripeConnectContext = (org) =>
  Boolean(
    org?.connected_account_id ||
    org?.test_connected_account_id ||
    typeof org?.destinationAccount === 'boolean'
  )

export default function AssignModalForm({
  ticketType,
  event,
  eventPackage,
  selectedSeatIds,
  data,
  initialAssignTo,
  initialExistingUser,
  initialPaymentMethod,
  sourceOrderId,
  addSeatsToOrderId,
  addCustomFeeToOrderId,
  sourceOrderTotal,
  sourceOrderPricingTier,
  sourceOrderCustomFeeIds,
  sourceOrderBuyerTypeId,
  reloadAssignments,
  stripeAccountContext,
  onStripeAccountContextChange,
}) {
  const isFlexPackAssignment = Boolean(eventPackage?.flexPackMode)
  const isAccessPassAssignment = Boolean(eventPackage?.accessPassMode)
  const isSupplementalCustomFee = Boolean(addCustomFeeToOrderId)
  const navigate = useNavigate()

  // Modal actions
  const { closeModal, openModal } = useModal()
  // AuthService.getOrg() parses localStorage each call and returns a new array/object,
  // which can cause effects/memos to re-run on every render. Memoize it for modal lifetime.
  const organizations = useMemo(() => AuthService.getOrg() || [], [])
  const organization = organizations?.[0]
  const terminalInfo = AuthService.getProfile()

  let stripePromise

  // Prefer deriving the connected account from the item being sold (event/package),
  // since admins can belong to multiple orgs and AuthService.getOrg()[0] may not match.
  const isProd =
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_STAGE === 'production'

  const paymentOrgFromSession = useMemo(() => {
    const targetOrgUUID =
      event?.organizationUUID || eventPackage?.organizationUUID
    if (!targetOrgUUID || !Array.isArray(organizations)) return undefined
    return organizations.find((org) => org?.uuid === targetOrgUUID)
  }, [event?.organizationUUID, eventPackage?.organizationUUID, organizations])

  const eventOrg = event?.organization
  const packageOrg = eventPackage?.organization
  const fallbackOrg = organization
  const paymentOrg = [eventOrg, packageOrg, paymentOrgFromSession, fallbackOrg]
    .filter(Boolean)
    .reduce((selected, candidate) => {
      if (!selected) return candidate
      if (
        !hasStripeConnectContext(selected) &&
        hasStripeConnectContext(candidate)
      )
        return candidate
      return selected
    }, null)
  const organizationUUIDForAssignment =
    event?.organizationUUID ||
    event?.organization?.uuid ||
    eventPackage?.organizationUUID ||
    eventPackage?.organization?.uuid ||
    paymentOrg?.uuid ||
    organization?.uuid

  // Resolve the org from AuthService (it has organization_fee_config populated)
  // since event/package org objects are not always fully populated.
  const feeConfigOrg = useMemo(() => {
    if (!paymentOrg) return organization
    const byUuid =
      paymentOrg?.uuid &&
      organizations?.find((o) => o?.uuid && o.uuid === paymentOrg.uuid)
    if (byUuid) return byUuid
    const byId =
      paymentOrg?.id &&
      organizations?.find((o) => o?.id && o.id === paymentOrg.id)
    return byId || paymentOrg || organization
  }, [paymentOrg, organization, organizations])

  const organizationFeeConfig = feeConfigOrg?.organization_fee_config

  const connectedAccountId = isProd
    ? paymentOrg?.connected_account_id
    : paymentOrg?.test_connected_account_id

  const destinationChargesEnabled = Boolean(
    feeConfigOrg?.destinationAccount ?? paymentOrg?.destinationAccount
  )

  // Use parent's Stripe account context (platform when destination charges, else connected account).
  // When stripeAccountContext is null, PIs are on platform and Stripe.js must not use stripeAccount.
  stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_KEY,
    stripeAccountContext ? { stripeAccount: stripeAccountContext } : undefined
  )

  // track overlapping modals
  const [isAnotherModalOpen, setIsAnotherModalOpen] = useState(false)
  const [currentSelectedSeatIds, setCurrentSelectedSeatIds] = useState(
    selectedSeatIds || {}
  )
  const [currentData, setCurrentData] = useState(data || {})
  const [accessPassQuantity, setAccessPassQuantity] = useState(() => {
    if (!eventPackage?.accessPassMode) return 1
    const seatCount = Object.keys(selectedSeatIds?.seats || {}).length
    return Math.max(1, seatCount || 1)
  })

  const applyAccessPassQuantity = (nextQuantity) => {
    if (!isAccessPassAssignment) return
    const qty = Math.max(
      1,
      Math.min(100, Number.parseInt(nextQuantity, 10) || 1)
    )
    setAccessPassQuantity(qty)
    const eventCount = Array.isArray(eventPackage?.events)
      ? eventPackage.events.length
      : 0
    const passName = eventPackage?.name || 'Access Pass'
    const eventLabel = `${eventCount} event${eventCount === 1 ? '' : 's'}`
    const seats = {}
    const seatIds = {}
    for (let i = 1; i <= qty; i += 1) {
      const id = `access-pass-item-${i}`
      seatIds[id] = true
      seats[id] = {
        sectionNumber: passName,
        rowNumber: 'PASS',
        seatNumber: qty === 1 ? eventLabel : `#${i}`,
      }
    }
    setCurrentSelectedSeatIds({ seats: seatIds })
    setCurrentData({ seats, sections: {} })
  }

  const [step, setStep] = useState(
    addSeatsToOrderId || isSupplementalCustomFee ? 2 : sourceOrderId ? 2 : 0
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [compLabels, setCompLabels] = useState([])
  const [selectedCompLabel, setSelectedCompLabel] = useState()
  const [buyerTypes, setBuyerTypes] = useState([])
  const [selectedBuyerType, setSelectedBuyerType] = useState()
  const [customFeeRows, setCustomFeeRows] = useState([])
  const [donationEvaluation, setDonationEvaluation] = useState(null)
  const [selectedDonationRuleId, setSelectedDonationRuleId] = useState('')
  const [configuredDonations, setConfiguredDonations] = useState([])
  const [donationAmount, setDonationAmount] = useState(0)
  const [donationExternalPending, setDonationExternalPending] = useState(false)
  const [customFeesList, setCustomFeesList] = useState([])
  const customFeeRowIdRef = useRef(0)
  const sourceCustomFeesHydratedRef = useRef(false)
  const lastHydratedBuyerTypeKeyRef = useRef(null)
  const [clientSecret, setClientSecret] = useState()
  const [initialClientSecret, setInitialClientSecret] = useState()
  const [card, setCard] = useState()
  const isComplimentary = ticketType === 'comp'
  const [stripe, setStripe] = useState()

  const [isLoading, setIsLoading] = useState(true)
  const [userType, setUserType] = useState('existing-user')
  // one-time or payment-plan
  const [paymentSchedule, setPaymentSchedule] = useState('one-time')
  const [existingUser, setExistingUser] = useState(initialExistingUser)
  // flag for existing user has changed
  const [initialExistingUserState, setInitialExistingUser] =
    useState(initialExistingUser)
  const [savedCards, setSavedCards] = useState()
  const [isLoadingCards, setIsLoadingCards] = useState(false)
  const [selectedCard, setSelectedCard] = useState()
  const [selectedCardOpt, setSelectedCardOpt] = useState('saved-card')
  const lastPaymentIntentKeyRef = useRef(null)
  const inFlightPaymentIntentKeyRef = useRef(null)
  const terminalInstanceRef = useRef(null)
  const isCollectingPaymentRef = useRef(false)
  const collectTimeoutRef = useRef(null)
  const paymentIntentIdRef = useRef(null)
  const terminalTimedOutRef = useRef(false)
  // Reuse a succeeded saved-card charge if order create fails (avoids double charge).
  const succeededSavedCardChargeRef = useRef(null)
  // Seats are held out of inventory before any card is charged, so another
  // buyer can't take them while the operator is still working the sale.
  const seatHoldIdRef = useRef(null)
  const seatHoldSeatsKeyRef = useRef(null)
  const seatHoldConsumedRef = useRef(false)
  // Set when this form is replaced by a modal that still submits the assignment,
  // so unmounting is a handoff rather than the operator abandoning the sale.
  const seatHoldHandedOffRef = useRef(false)
  const pendingOrderIdRef = useRef(null)
  const assignmentCheckoutKeyRef = useRef(
    `${isSupplementalCustomFee ? `supplemental-fee-${addCustomFeeToOrderId}` : 'assignment'}-${Date.now()}-${Math.random().toString(36).slice(2)}`
  )
  const [terminalPaymentStatus, setTerminalPaymentStatus] = useState('')

  // payment plan states
  const [installmentMonths, setInstallmentMonths] = useState()
  const [paymentPlans, setPaymentPlans] = useState({})
  const [stripeTerminal, setStripeTerminal] = useState()

  // Lifted state from Pricing
  // payment method
  // - online - invoice, pos, credit card
  // - in-person - cash, check, team-credit, e-transfer, pos
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod)

  const isSavedCardOneTimeSelection = useMemo(() => {
    return (
      paymentMethod === 'CARD' &&
      paymentSchedule === 'one-time' &&
      selectedCardOpt === 'saved-card' &&
      Boolean(existingUser)
    )
  }, [paymentMethod, paymentSchedule, selectedCardOpt, existingUser])

  const [price, setPrice] = useState(isSupplementalCustomFee ? '0' : '')

  // Access-pass "comp" must follow the selected pricing tier, not template.price.
  // Offline-only templates store price as null/0 while still exposing paid tiers.
  const selectedAccessPassPrice =
    Number(String(price ?? '').trim() !== '' ? price : eventPackage?.price) || 0
  const isComplimentaryAccessPassAssignment =
    isAccessPassAssignment && selectedAccessPassPrice === 0
  // Comp is $0 to the buyer, so a paid flex pack assigned that way is still
  // complimentary. Pack price alone only catches $0 sponsorship packs.
  const isComplimentaryFlexPackAssignment =
    isFlexPackAssignment &&
    (isCompFlexPack(eventPackage) || paymentMethod === 'COMP')
  const isComplimentaryStandaloneAssignment =
    isComplimentaryFlexPackAssignment || isComplimentaryAccessPassAssignment

  // all price tiers
  const [priceTiers, setPriceTiers] = useState(
    eventPackage ? eventPackage.pricingTiers : []
  )
  const normalizedSourceOrderPricingTier = useMemo(() => {
    if (!sourceOrderPricingTier) return undefined
    if (typeof sourceOrderPricingTier === 'string') {
      const trimmed = sourceOrderPricingTier.trim()
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return JSON.parse(trimmed)
        } catch (_) {
          return sourceOrderPricingTier
        }
      }
      return sourceOrderPricingTier
    }
    return sourceOrderPricingTier
  }, [sourceOrderPricingTier])
  const selectedPricingTier = useMemo(() => {
    if (!eventPackage || !Array.isArray(priceTiers)) return undefined
    const numericPrice = Number(price)
    if (!Number.isFinite(numericPrice)) return undefined
    return priceTiers.find((tier) => Number(tier?.price) === numericPrice)
  }, [eventPackage, priceTiers, price])

  // Lifted state from AssignToGroup
  const initialAssignToDefaults = useMemo(
    () => ({
      organizationName: initialAssignTo?.organizationName || '',
      firstName: initialAssignTo?.firstName || '',
      lastName: initialAssignTo?.lastName || '',
      email: initialAssignTo?.email || '',
      phoneNumber: initialAssignTo?.phoneNumber || null,
    }),
    [initialAssignTo]
  )
  const [assignTo, setAssignTo] = useState(initialAssignToDefaults)
  const [isValidEmail, setIsValidEmail] = useState(true)
  const [isValidNumber, setIsValidNumber] = useState(true)
  const [isCardValid, setIsCardValid] = useState(false)
  const [reader, setReader] = useState(null)

  // due date for invoice
  const [dueDateOpt, setDueDateOpt] = useState()

  const [customDueDate, setCustomDueDate] = useState()

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    variant: 'danger',
    message: '',
  })

  // Memorize total seats calculation
  const totalSeats = useMemo(() => {
    let total = 0

    if (currentSelectedSeatIds?.seats) {
      total += Object.keys(currentSelectedSeatIds.seats).length
    }

    if (currentSelectedSeatIds?.gaSeats) {
      Object.entries(currentSelectedSeatIds.gaSeats).forEach(
        ([sectionId, seatIds]) => {
          total += seatIds.length
        }
      )
    }

    return total
  }, [currentSelectedSeatIds])

  useEffect(() => {
    if (!sourceOrderId || !eventPackage) return
    if (price !== '') return
    if (!Array.isArray(priceTiers) || !priceTiers.length) return

    const sourceTier = normalizedSourceOrderPricingTier
    if (!sourceTier) return

    const sourceTierId =
      sourceTier &&
      typeof sourceTier === 'object' &&
      sourceTier?.id !== undefined
        ? String(sourceTier.id)
        : ''
    const sourceTierName =
      typeof sourceTier === 'string'
        ? sourceTier.trim().toLowerCase()
        : String(sourceTier?.name || '')
            .trim()
            .toLowerCase()
    const sourceTierPrice = Number(
      typeof sourceTier === 'number' || typeof sourceTier === 'string'
        ? sourceTier
        : sourceTier?.price
    )

    const matchedTier = priceTiers.find((tier) => {
      if (sourceTierId && String(tier?.id) === sourceTierId) return true
      if (
        sourceTierName &&
        String(tier?.name || '')
          .trim()
          .toLowerCase() === sourceTierName
      )
        return true
      if (
        Number.isFinite(sourceTierPrice) &&
        Number(tier?.price) === sourceTierPrice
      )
        return true
      return false
    })

    if (matchedTier?.price !== undefined && matchedTier?.price !== null) {
      setPrice(String(matchedTier.price))
      return
    }

    if (Number.isFinite(sourceTierPrice)) {
      const hasMatchingPrice = priceTiers.some(
        (tier) => Number(tier?.price) === sourceTierPrice
      )
      if (hasMatchingPrice) setPrice(String(sourceTierPrice))
    }
  }, [
    sourceOrderId,
    eventPackage,
    normalizedSourceOrderPricingTier,
    priceTiers,
    price,
  ])

  useEffect(() => {
    const totalFromSourceOrder = Number(sourceOrderTotal)
    if (!sourceOrderId) return
    if (sourceOrderPricingTier) return
    if (!Number.isFinite(totalFromSourceOrder) || totalFromSourceOrder <= 0)
      return
    if (!totalSeats) return
    if (price !== '') return

    setPrice((totalFromSourceOrder / totalSeats).toFixed(2))
  }, [
    sourceOrderId,
    sourceOrderTotal,
    sourceOrderPricingTier,
    totalSeats,
    price,
  ])

  useEffect(() => {
    setCurrentSelectedSeatIds(selectedSeatIds || {})
    setCurrentData(data || {})
  }, [selectedSeatIds, data])

  // Price breakdown
  const pricingObject = useMemo(() => {
    const basePricingObject = event
      ? groupPurchasePricing(
          price,
          event,
          totalSeats,
          ticketType,
          paymentMethod,
          organizationFeeConfig
        )
      : packagePricing(
          eventPackage,
          totalSeats,
          paymentMethod,
          true,
          price,
          paymentOrg,
          organizationFeeConfig,
          {
            source: ORDER_SOURCE.TICKET_ASSIGNMENT,
            productType: isAccessPassAssignment
              ? PRODUCT_TYPE.ACCESS_PASS
              : PRODUCT_TYPE.SEASON_PACKAGES,
          }
        )

    if (isAccessPassAssignment && basePricingObject) {
      const priceNum = selectedAccessPassPrice
      if (priceNum === 0 || isComplimentaryAccessPassAssignment) {
        return {
          ...basePricingObject,
          serviceFee: 0,
          serviceFeePerTicket: 0,
          paymentProcessingFee: 0,
          paymentProcessingFeePerTicket: 0,
          purchasePriceWithTaxAndFees: 0,
          yourPayout: 0,
        }
      }

      const currentServiceFee = Number(basePricingObject?.serviceFee) || 0
      // Match website access-pass fallback ($1) when org fee config has no rule.
      // Flat package/pass overrides (including $0) must not be replaced.
      if (basePricingObject?.isFlatServiceFee || currentServiceFee > 0) {
        return basePricingObject
      }

      const targetServiceFee = 1
      const currentProcessingFee =
        Number(basePricingObject?.paymentProcessingFee) || 0
      const currentTotal =
        Number(basePricingObject?.purchasePriceWithTaxAndFees) || 0
      const subtotalWithoutProcessing = Math.max(
        0,
        currentTotal - currentProcessingFee
      )
      const subtotalWithTargetServiceFee = Math.max(
        0,
        subtotalWithoutProcessing - currentServiceFee + targetServiceFee
      )
      const recalculatedProcessingFee = getProcessingFeeFromChargeAmount(
        paymentMethod,
        subtotalWithTargetServiceFee
      )
      const ticketCount = Number(basePricingObject?.ticketCount) || 0
      const recalculatedTotal =
        subtotalWithTargetServiceFee + recalculatedProcessingFee

      return {
        ...basePricingObject,
        serviceFee: targetServiceFee,
        serviceFeePerTicket:
          ticketCount > 0
            ? truncateToTwoDecimals(targetServiceFee / ticketCount)
            : targetServiceFee,
        paymentProcessingFee: recalculatedProcessingFee,
        paymentProcessingFeePerTicket:
          ticketCount > 0
            ? truncateToTwoDecimals(recalculatedProcessingFee / ticketCount)
            : recalculatedProcessingFee,
        purchasePriceWithTaxAndFees: truncateToTwoDecimals(recalculatedTotal),
        yourPayout: truncateToTwoDecimals(
          Math.max(0, subtotalWithTargetServiceFee - targetServiceFee)
        ),
      }
    }

    if (!isFlexPackAssignment || !basePricingObject) {
      return basePricingObject
    }

    if (isComplimentaryFlexPackAssignment) {
      return {
        ...basePricingObject,
        serviceFee: 0,
        serviceFeePerTicket: 0,
        paymentProcessingFee: 0,
        paymentProcessingFeePerTicket: 0,
        purchasePriceWithTaxAndFees: 0,
        yourPayout: 0,
      }
    }

    const targetServiceFee = getFlexPackVoucherServiceFee(eventPackage)
    const currentServiceFee = Number(basePricingObject?.serviceFee) || 0
    const currentProcessingFee =
      Number(basePricingObject?.paymentProcessingFee) || 0
    const currentTotal =
      Number(basePricingObject?.purchasePriceWithTaxAndFees) || 0
    const subtotalWithoutProcessing = Math.max(
      0,
      currentTotal - currentProcessingFee
    )
    const subtotalWithTargetServiceFee = Math.max(
      0,
      subtotalWithoutProcessing - currentServiceFee + targetServiceFee
    )

    const recalculatedProcessingFee = getProcessingFeeFromChargeAmount(
      paymentMethod,
      subtotalWithTargetServiceFee
    )

    const ticketCount = Number(basePricingObject?.ticketCount) || 0
    const recalculatedTotal =
      subtotalWithTargetServiceFee + recalculatedProcessingFee

    return {
      ...basePricingObject,
      serviceFee: targetServiceFee,
      serviceFeePerTicket:
        ticketCount > 0
          ? truncateToTwoDecimals(targetServiceFee / ticketCount)
          : targetServiceFee,
      paymentProcessingFee: recalculatedProcessingFee,
      paymentProcessingFeePerTicket:
        ticketCount > 0
          ? truncateToTwoDecimals(recalculatedProcessingFee / ticketCount)
          : recalculatedProcessingFee,
      purchasePriceWithTaxAndFees: truncateToTwoDecimals(recalculatedTotal),
      // Org keeps ticket face value only; platform retains service + processing
      // via destination application_fee (same as website flex-pack checkout).
      yourPayout: truncateToTwoDecimals(
        Math.max(0, subtotalWithTargetServiceFee - targetServiceFee)
      ),
    }
  }, [
    price,
    event,
    totalSeats,
    ticketType,
    paymentMethod,
    eventPackage,
    paymentOrg,
    organizationFeeConfig,
    isFlexPackAssignment,
    isAccessPassAssignment,
    isComplimentaryAccessPassAssignment,
    isComplimentaryFlexPackAssignment,
    selectedAccessPassPrice,
  ])

  const customFeesApply =
    paymentMethod !== 'COMP' && paymentMethod !== 'RESERVED'

  const customFeePercentBase = useMemo(
    () => pricingObject?.purchasePriceWithTaxAndFees ?? 0,
    [pricingObject?.purchasePriceWithTaxAndFees]
  )

  const totalCustomFeeAmount = useMemo(() => {
    if (!customFeesApply) return 0
    let sum = 0
    for (const row of customFeeRows) {
      if (row.feeId == null || row.feeId === '') continue
      const feeEntity = customFeesList.find(
        (f) => String(f.id) === String(row.feeId)
      )
      sum += resolveCustomFeeLineAmount(feeEntity, customFeePercentBase)
    }
    return Math.round(sum * 100) / 100
  }, [customFeeRows, customFeesList, customFeePercentBase, customFeesApply])

  const customFeeSummaryLines = useMemo(() => {
    if (!customFeesApply) return []
    const lines = []
    for (const row of customFeeRows) {
      if (row.feeId == null || row.feeId === '') continue
      const feeEntity = customFeesList.find(
        (f) => String(f.id) === String(row.feeId)
      )
      const raw = resolveCustomFeeLineAmount(feeEntity, customFeePercentBase)
      const amount = Math.round(raw * 100) / 100
      if (amount <= 0) continue
      const attrs = feeEntity?.attributes ?? feeEntity
      const name = attrs?.name ?? 'Custom fee'
      lines.push({ rowId: row.id, feeId: row.feeId, name, amount })
    }
    return lines
  }, [customFeeRows, customFeesList, customFeePercentBase, customFeesApply])

  const customFeeIdsForInvoice = useMemo(() => {
    if (!customFeesApply) return []
    return customFeeRows
      .map((r) => r.feeId)
      .filter((id) => id != null && id !== '')
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n))
  }, [customFeeRows, customFeesApply])

  const customFeeLinesForInvoice = useMemo(() => {
    if (!customFeesApply) return []
    return customFeeSummaryLines.map((line) => ({
      ...(line.feeId != null && line.feeId !== ''
        ? { id: Number(line.feeId) }
        : {}),
      name: line.name,
      amount: line.amount,
    }))
  }, [customFeesApply, customFeeSummaryLines])

  const activeDonationRule = useMemo(() => {
    const unmet = donationEvaluation?.unmetRules || []
    if (!unmet.length) return null
    if (unmet.length === 1) return unmet[0]
    return unmet.find(
      (rule) => String(rule.id) === String(selectedDonationRuleId)
    )
  }, [donationEvaluation, selectedDonationRuleId])

  const donationChargeAmount = useMemo(() => {
    if (!donationEvaluation?.requiresDonation) return 0

    const unmet = donationEvaluation?.unmetRules || []
    if (unmet.length > 1) {
      return configuredDonations
        .filter((entry) => !entry.externalPending)
        .reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0)
    }

    if (donationExternalPending) return 0
    if (!activeDonationRule) return 0
    return Number(donationAmount) || 0
  }, [
    donationEvaluation,
    configuredDonations,
    donationExternalPending,
    activeDonationRule,
    donationAmount,
  ])

  // Card/invoice/POS totals include processing fee on the charge base. Custom fees and bundled
  // donation amounts increase that base, so processing fee is recomputed from the extended subtotal.
  // Donations stay as separate order-summary lines but are included in the processing-fee base.
  const effectivePricingObject = useMemo(() => {
    if (!pricingObject) return pricingObject

    const hasCustomFees = customFeesApply && totalCustomFeeAmount > 0
    const hasDonationCharge = donationChargeAmount > 0
    if (!hasCustomFees && !hasDonationCharge) return pricingObject
    if (!isAssignCardLikePaymentMethod(paymentMethod)) return pricingObject

    const basePf = Number(pricingObject.paymentProcessingFee) || 0
    const totalWithFees = Number(pricingObject.purchasePriceWithTaxAndFees) || 0
    const baseSubtotal = totalWithFees - basePf
    if (!Number.isFinite(baseSubtotal) || baseSubtotal < 0) return pricingObject

    const customFeeAdd = hasCustomFees ? totalCustomFeeAmount : 0
    const subtotalInPurchaseTotal = baseSubtotal + customFeeAdd
    const extendedSubtotalForPf =
      subtotalInPurchaseTotal + (hasDonationCharge ? donationChargeAmount : 0)

    const newPf = getProcessingFeeFromChargeAmount(
      paymentMethod,
      extendedSubtotalForPf
    )

    const seatCount = Number(totalSeats) || 0
    const newTotal = subtotalInPurchaseTotal + newPf

    return {
      ...pricingObject,
      paymentProcessingFee: newPf,
      paymentProcessingFeePerTicket:
        seatCount > 0
          ? truncateToTwoDecimals(newPf / seatCount)
          : pricingObject.paymentProcessingFeePerTicket,
      purchasePriceWithTaxAndFees: truncateToTwoDecimals(newTotal),
      processingFeeIncludesDonation: hasDonationCharge,
    }
  }, [
    pricingObject,
    customFeesApply,
    totalCustomFeeAmount,
    donationChargeAmount,
    paymentMethod,
    eventPackage,
    event,
    totalSeats,
  ])

  const handleApplyDonation = useCallback((entry) => {
    setConfiguredDonations((prev) => {
      const next = prev.filter(
        (existing) => String(existing.ruleId) !== String(entry.ruleId)
      )
      return [...next, entry]
    })
    setSelectedDonationRuleId('')
    setDonationAmount(0)
    setDonationExternalPending(false)
  }, [])

  const donationInvoiceFields = useMemo(() => {
    if (!donationEvaluation?.requiresDonation) return {}

    const unmet = donationEvaluation?.unmetRules || []
    const requirementYear = donationEvaluation.requirementYear

    if (unmet.length > 1) {
      if (!configuredDonations.length) return {}
      return {
        donations: configuredDonations.map((entry) => ({
          ruleId: entry.ruleId,
          amount: entry.externalPending ? 0 : Number(entry.amount) || 0,
          externalPending: entry.externalPending,
          requirementYear,
        })),
      }
    }

    if (!activeDonationRule) return {}
    return {
      donation: {
        ruleId: activeDonationRule.id,
        amount: donationExternalPending ? 0 : Number(donationAmount) || 0,
        externalPending: donationExternalPending,
        requirementYear,
      },
    }
  }, [
    donationEvaluation,
    configuredDonations,
    activeDonationRule,
    donationAmount,
    donationExternalPending,
  ])

  const amountDue = useMemo(() => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    const base = getAmountDue(
      effectivePricingObject,
      paymentMethod,
      hasPaymentPlan,
      paymentPlans
    )
    const extra =
      customFeesApply && !isAssignCardLikePaymentMethod(paymentMethod)
        ? totalCustomFeeAmount
        : 0
    const n = Number(base) + extra + donationChargeAmount
    return Number.isFinite(n) ? n : 0
  }, [
    effectivePricingObject,
    paymentMethod,
    paymentPlans,
    totalCustomFeeAmount,
    customFeesApply,
    donationChargeAmount,
  ])

  // Validation logic
  useEffect(() => {
    let isFormValid = false
    if (step === 0) {
      // if user type is existing user, skip checking form
      isFormValid = Boolean(existingUser)
    } else if (step === 1) {
      // Step 1 validation: first name, last name, and email required
      isFormValid =
        assignTo.firstName.trim() !== '' &&
        assignTo.lastName.trim() !== '' &&
        assignTo.email.trim() !== '' &&
        isValidEmail &&
        isValidNumber
    } else {
      // get paymentOption
      const option = getPaymentOption(paymentMethod)

      // Step 2 validation: price (can be zero now to support iceDogs comps) and payment method required
      const isPriceValid =
        paymentMethod === 'COMP' || paymentMethod === 'RESERVED'
          ? true
          : !isNaN(Number(parseFloat(price))) ||
            (customFeesApply && totalCustomFeeAmount > 0)

      const hasPaymentMethod = Boolean(paymentMethod)

      const hasPaymentOption = Boolean(option)

      const hasPaymentPlan =
        paymentSchedule === 'one-time'
          ? true
          : Object.keys(paymentPlans).length > 0

      const skipPaymentIntentCheck = Boolean(
        [
          ...inPersonPaymentMethods,
          'COMP',
          'RESERVED',
          'INVOICE',
          'POS',
        ].includes(paymentMethod)
      )

      const checkPaymentIntent =
        skipPaymentIntentCheck ||
        (Number(parseFloat(price)) === 0 && totalCustomFeeAmount <= 0)
          ? true
          : paymentMethod === 'CARD' &&
            (isSavedCardOneTimeSelection || Boolean(clientSecret))

      const hasDueDate =
        paymentMethod === 'INVOICE'
          ? Boolean(dueDateOpt) &&
            (dueDateOpt !== 'custom' || Boolean(customDueDate))
          : true

      console.log(
        'step 2 payment intent',
        checkPaymentIntent,
        Boolean(clientSecret),
        isPriceValid,
        price
      )

      // Validate label selection against the currently selected payment method.
      // Reserved is a hold, not a comp — do not require a hidden Comp type.
      const isCompBuyerValid = isAssignmentLabelSelectionValid({
        paymentMethod,
        compLabels,
        selectedCompLabel,
        buyerTypes,
        selectedBuyerType,
      })
      // is credit card valid
      // - credit card valid
      // - saved card selected
      const isPaymentMethodValid =
        paymentMethod === 'CARD' ? Boolean(selectedCard || isCardValid) : true

      const unmetDonationRules = donationEvaluation?.unmetRules || []
      const isMultiRuleDonation = unmetDonationRules.length > 1
      const configuredDonationRuleIds = new Set(
        configuredDonations.map((entry) => String(entry.ruleId))
      )
      const allDonationRulesConfigured =
        !isMultiRuleDonation ||
        unmetDonationRules.every((rule) =>
          configuredDonationRuleIds.has(String(rule.id))
        )
      const requiresDonationSelection =
        donationEvaluation?.requiresDonation &&
        isMultiRuleDonation &&
        !allDonationRulesConfigured
      const donationMinimum = Number(activeDonationRule?.minimum) || 0
      const isDonationAmountValid = donationEvaluation?.requiresDonation
        ? isMultiRuleDonation
          ? allDonationRulesConfigured &&
            configuredDonations.every((entry) => {
              const rule = unmetDonationRules.find(
                (candidate) => String(candidate.id) === String(entry.ruleId)
              )
              const minimum = Number(rule?.minimum) || 0
              return entry.externalPending || Number(entry.amount) >= minimum
            })
          : donationExternalPending || Number(donationAmount) >= donationMinimum
        : true

      isFormValid =
        isPriceValid &&
        (!isSupplementalCustomFee || totalCustomFeeAmount > 0) &&
        hasPaymentMethod &&
        hasPaymentOption &&
        checkPaymentIntent &&
        hasPaymentPlan &&
        hasDueDate &&
        isCompBuyerValid &&
        isPaymentMethodValid &&
        !requiresDonationSelection &&
        isDonationAmountValid
    }
    setIsValid(isFormValid)
  }, [
    step,
    price,
    paymentMethod,
    assignTo,
    isValidEmail,
    isValidNumber,
    selectedBuyerType,
    selectedCompLabel,
    buyerTypes,
    compLabels,
    paymentSchedule,
    userType,
    isCardValid,
    selectedCard,
    clientSecret,
    paymentPlans,
    isSavedCardOneTimeSelection,
    dueDateOpt,
    customDueDate,
    donationEvaluation,
    selectedDonationRuleId,
    configuredDonations,
    activeDonationRule,
    donationAmount,
    donationExternalPending,
    existingUser,
    customFeesApply,
    totalCustomFeeAmount,
    isSupplementalCustomFee,
  ])

  // Reset buyer/payment state only when switching to the new-user path.
  // Do not depend on existingUser — selecting a user while userType is
  // 'new-user' used to immediately clear the selection and leave Next disabled.
  useEffect(() => {
    setClientSecret()
    if (userType === 'existing-user') {
      setSelectedCardOpt('saved-card')
      return
    }

    setSelectedCardOpt('new-card')
    setExistingUser()
    setAssignTo({
      organizationName: initialAssignToDefaults.organizationName,
      firstName: initialAssignToDefaults.firstName,
      lastName: initialAssignToDefaults.lastName,
      email: initialAssignToDefaults.email,
      phoneNumber: initialAssignToDefaults.phoneNumber,
    })
    setSavedCards()
    // intentionally only userType — initialAssignToDefaults read from closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType])

  useEffect(() => {
    // Reset valid email error state when email changes
    setIsValidEmail(true)
  }, [assignTo.email])

  useEffect(() => {
    // Reset valid phone number error state when phone number changes
    setIsValidNumber(true)
  }, [assignTo.phoneNumber])

  // reset payment plans when payment schedule (one time/payment plans) changes
  useEffect(() => {
    setPaymentPlans({})
    setInstallmentMonths()
  }, [paymentSchedule])

  // reset payment schedule (one-time/payment plans) when payment method (card, check etc.) changes
  useEffect(() => {
    setPaymentSchedule('one-time')
    if (isAssignmentCompLabelMethod(paymentMethod)) {
      setBuyerTypes([])
      loadCompLabels()
    } else {
      setCompLabels([])
      loadBuyerTypes()
    }

    // reset when payment method changes
    setIsCardValid(false)
    setSelectedCardOpt('saved-card')
    setClientSecret()
    lastPaymentIntentKeyRef.current = null
    inFlightPaymentIntentKeyRef.current = null

    // reset selected buyer/comp types depending on if payment method is comp
    if (!sourceOrderBuyerTypeId) {
      if (isAssignmentCompLabelMethod(paymentMethod)) setSelectedBuyerType()
      else setSelectedCompLabel()
    }
  }, [paymentMethod, sourceOrderBuyerTypeId])

  // new existing user
  useEffect(() => {
    setIsCardValid(false)
    setSelectedCard()
    // get saved cards
    if (existingUser) {
      // if payment method is credit card
      if (paymentMethod === 'CARD') {
        // if different user
        // or haven't gotten saved cards yet (first time)
        if (!isEqual(existingUser, initialExistingUserState) || !savedCards) {
          loadSavedCards(existingUser?.email)
        }
      }
    }
  }, [existingUser, initialExistingUserState, paymentMethod])

  useEffect(() => {
    if (existingUser) {
      const obj = {
        organizationName: '',
        firstName: existingUser?.firstName,
        lastName: existingUser?.lastName,
        email: existingUser?.email,
        phoneNumber: existingUser?.phoneNumber,
      }
      setAssignTo(obj)
    }
  }, [existingUser])

  useEffect(() => {
    if (!installmentMonths) return

    const splitBase = isAssignCardLikePaymentMethod(paymentMethod)
      ? (effectivePricingObject?.purchasePriceWithTaxAndFees ?? 0)
      : (pricingObject?.purchasePriceWithTaxAndFees ?? 0) +
        (customFeesApply ? totalCustomFeeAmount : 0)

    const paymentCalculations = calculations.splitAmount(
      splitBase,
      installmentMonths
    )

    const plans = [...Array(installmentMonths)].reduce((acc, _, index) => {
      const id = index + 1

      acc[id] = {
        id,
        amount: paymentCalculations[index].toFixed(2),
        dueDate: moment()
          .startOf('day')
          .add(id === 1 ? index : id - 1, 'months')
          .toDate(),
      }

      return acc
    }, {})

    setPaymentPlans(plans)
  }, [
    installmentMonths,
    pricingObject?.purchasePriceWithTaxAndFees,
    effectivePricingObject?.purchasePriceWithTaxAndFees,
    paymentMethod,
    totalCustomFeeAmount,
    customFeesApply,
  ])

  useEffect(() => {
    if (step !== 2 || !organizationUUIDForAssignment) return
    getCustomFees(organizationUUIDForAssignment)
      .then((res) => setCustomFeesList(res?.data?.data ?? []))
      .catch((err) => console.error(err))
  }, [step, organizationUUIDForAssignment])

  useEffect(() => {
    if (sourceCustomFeesHydratedRef.current) return
    if (!customFeesList.length) return

    const seedIds = assignmentCustomFeeSeedIds({
      eventPackage,
      sourceOrderCustomFeeIds,
      sourceOrderId,
      addSeatsToOrderId,
      addCustomFeeToOrderId,
      isFlexPackAssignment,
      isAccessPassAssignment,
    })
    if (!seedIds.length) return

    const validIds = seedIds.filter((feeId) =>
      customFeesList.some((fee) => String(fee.id) === String(feeId))
    )
    if (!validIds.length) return

    sourceCustomFeesHydratedRef.current = true
    const rows = validIds.map((feeId) => {
      customFeeRowIdRef.current += 1
      return { id: customFeeRowIdRef.current, feeId: String(feeId) }
    })
    setCustomFeeRows(rows)
  }, [
    sourceOrderCustomFeeIds,
    customFeesList,
    eventPackage,
    sourceOrderId,
    addSeatsToOrderId,
    addCustomFeeToOrderId,
    isFlexPackAssignment,
    isAccessPassAssignment,
  ])

  useEffect(() => {
    if (!sourceOrderBuyerTypeId) return

    const buyerTypeId = String(sourceOrderBuyerTypeId)

    if (isAssignmentCompLabelMethod(paymentMethod)) {
      const hydrationKey = `${paymentMethod}:${buyerTypeId}`
      if (lastHydratedBuyerTypeKeyRef.current === hydrationKey) return

      const match = compLabels.find((type) => String(type.id) === buyerTypeId)
      if (!match) return

      setSelectedCompLabel(buyerTypeId)
      lastHydratedBuyerTypeKeyRef.current = hydrationKey
      return
    }

    const hydrationKey = `${paymentMethod || 'pending'}:${buyerTypeId}`
    if (lastHydratedBuyerTypeKeyRef.current === hydrationKey) return

    const match = buyerTypes.find((type) => String(type.id) === buyerTypeId)
    if (!match) return

    setSelectedBuyerType(buyerTypeId)
    lastHydratedBuyerTypeKeyRef.current = hydrationKey
  }, [sourceOrderBuyerTypeId, buyerTypes, compLabels, paymentMethod])

  const setPaymentDate = (date, id) => {
    let paymentPlansCopy = paymentPlans
    let keys = Object.keys(paymentPlans)

    // add 1 to month to payment dates
    for (let key of keys) {
      paymentPlansCopy[key].dueDate = new Date(
        moment(date)
          .startOf('day')
          .add(paymentPlansCopy[key].id - 1, 'months')
          .format()
      )
    }

    setPaymentPlans({ ...paymentPlansCopy })
  }

  const loadSavedCards = (userEmail) => {
    setIsLoadingCards(true)
    customerPaymentMethods(connectedAccountId, userEmail)
      .then((res) => {
        const methods = res?.data?.paymentMethods || res?.data?.data || []
        setSavedCards(Array.isArray(methods) ? methods : [])
      })
      .catch((err) => {
        console.error(err)
        setSavedCards([])
      })
      .finally(() => setIsLoadingCards(false))
  }

  // Only plain single-event assignments reserve from live inventory. Package
  // assigns reserve through the package service, and completing an existing
  // reserved order reuses seats that are already out of inventory.
  const seatHoldApplies = Boolean(
    event?.uuid && !eventPackage && !sourceOrderId && !addSeatsToOrderId
  )

  const releaseSeatHoldIfUnused = useCallback(async () => {
    const holdId = seatHoldIdRef.current
    const pendingOrderId = pendingOrderIdRef.current
    if (seatHoldConsumedRef.current) return
    seatHoldIdRef.current = null
    seatHoldSeatsKeyRef.current = null
    pendingOrderIdRef.current = null
    try {
      if (pendingOrderId) {
        await cancelPendingCheckoutOrder(pendingOrderId, 'abandoned')
      } else if (holdId) {
        await releaseAssignmentSeatHold(holdId)
      }
    } catch (err) {
      console.error('Failed to release pending checkout:', err)
    }
  }, [])

  /**
   * Take (or reuse) a hold on the current seat selection. Throws if the seats
   * are already gone, which keeps us from charging a card we can't fulfill.
   */
  const ensureSeatHold = useCallback(async () => {
    if (!seatHoldApplies || !currentSelectedSeatIds) return null

    const seatsKey = JSON.stringify(currentSelectedSeatIds)
    if (seatHoldIdRef.current && seatHoldSeatsKeyRef.current === seatsKey) {
      try {
        await refreshAssignmentSeatHold(seatHoldIdRef.current)
        return seatHoldIdRef.current
      } catch (err) {
        // Hold expired out from under us; fall through and take a fresh one.
        seatHoldIdRef.current = null
        seatHoldSeatsKeyRef.current = null
      }
    }

    if (seatHoldIdRef.current) {
      await releaseSeatHoldIfUnused()
    }

    const res = await holdAssignmentSeats({
      eventUUID: event.uuid,
      selectedSeats: currentSelectedSeatIds,
    })
    const holdId = res?.data?.data?.holdId
    if (!holdId) {
      throw new Error('Unable to hold the selected seats. Please try again.')
    }
    seatHoldIdRef.current = holdId
    seatHoldSeatsKeyRef.current = seatsKey
    return holdId
  }, [
    seatHoldApplies,
    currentSelectedSeatIds,
    event?.uuid,
    releaseSeatHoldIfUnused,
  ])

  // Take the hold as soon as the operator starts arranging payment, so seats
  // survive the pricing/discount conversation instead of only the charge.
  useEffect(() => {
    if (!seatHoldApplies || step !== 2) return
    if (seatHoldIdRef.current) return

    let cancelled = false
    ensureSeatHold().catch((err) => {
      if (cancelled) return
      console.error('Failed to hold seats for assignment:', err)
    })
    return () => {
      cancelled = true
    }
  }, [seatHoldApplies, step, ensureSeatHold])

  // Keep the hold alive while the modal is open and unsubmitted.
  useEffect(() => {
    if (!seatHoldApplies) return
    const interval = setInterval(() => {
      const holdId = seatHoldIdRef.current
      if (!holdId || seatHoldConsumedRef.current) return
      refreshAssignmentSeatHold(holdId).catch(() => {})
    }, 60000)
    return () => clearInterval(interval)
  }, [seatHoldApplies])

  // Abandoning the modal must put the seats straight back on sale.
  useEffect(() => {
    return () => {
      const holdId = seatHoldIdRef.current
      const pendingOrderId = pendingOrderIdRef.current
      if (seatHoldConsumedRef.current || seatHoldHandedOffRef.current) return
      if (pendingOrderId) {
        cancelPendingCheckoutOrder(pendingOrderId, 'abandoned').catch(() => {})
      } else if (holdId) {
        releaseAssignmentSeatHold(holdId).catch(() => {})
      }
    }
  }, [])

  const loadPaymentIntent = (requestKey, { force = false } = {}) => {
    const hasPriceInput =
      price !== '' && price != null && String(price).trim() !== ''
    if (!hasPriceInput && !(customFeesApply && totalCustomFeeAmount > 0)) {
      return Promise.resolve(null)
    }
    if (!requestKey) return Promise.resolve(null)
    if (
      !force &&
      (inFlightPaymentIntentKeyRef.current === requestKey ||
        lastPaymentIntentKeyRef.current === requestKey)
    ) {
      return Promise.resolve(clientSecret || null)
    }
    if (force) {
      lastPaymentIntentKeyRef.current = null
    }
    inFlightPaymentIntentKeyRef.current = requestKey
    // reset client secret
    setClientSecret()
    console.log('load intent')
    // Checks if payment plans are set
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    const makePurchaseDetailsForDraft = (selectedSeatIdsDraft, packageUUID) => {
      if (!packageUUID) return {}
      if (selectedSeatIdsDraft?.gaSeats) {
        const targetSectionId = Object.keys(selectedSeatIdsDraft.gaSeats)[0]
        const targetSeatIds = (
          selectedSeatIdsDraft.gaSeats[targetSectionId] || []
        ).filter(Boolean)
        const desiredTicketCount = targetSeatIds.length
        return {
          GA: true,
          sectionId: targetSectionId,
          desiredTicketCount,
          ...(targetSeatIds.length ? { targetSeatIds } : {}),
        }
      }
      if (selectedSeatIdsDraft?.seats) {
        return {
          GA: false,
          targetSeatIds: Object.keys(selectedSeatIdsDraft.seats),
        }
      }
      return {}
    }
    const safeNum = (value) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    }
    const ticketTotalForSnapshot = (cardLike) =>
      cardLike
        ? safeNum(effectivePricingObject?.purchasePriceWithTaxAndFees)
        : safeNum(pricingObject?.purchasePriceWithTaxAndFees) +
          (customFeesApply ? totalCustomFeeAmount : 0)
    const buildDraftPricingSnapshot = (cardLike, dueNow) => ({
      total: ticketTotalForSnapshot(cardLike) + donationChargeAmount,
      salesTax: safeNum(pricingObject?.salesTax),
      serviceFee: safeNum(pricingObject?.serviceFee),
      processingFee: safeNum(effectivePricingObject?.paymentProcessingFee),
      processingFeeIncludesDonation: donationChargeAmount > 0,
      facilityFee: safeNum(pricingObject?.ticketFacilityFee),
      chargeBackProtection: safeNum(pricingObject?.chargeBackProtection),
      net: safeNum(pricingObject?.yourPayout),
      ticketCount: safeNum(totalSeats),
      amountDueNow: safeNum(dueNow),
    })

    const assignment = {}
    assignment['assignTo'] = assignTo

    const baseDue = getAmountDue(
      effectivePricingObject,
      paymentMethod,
      hasPaymentPlan,
      paymentPlans
    )
    const customExtraForNonCardLike =
      customFeesApply && !isAssignCardLikePaymentMethod(paymentMethod)
        ? totalCustomFeeAmount
        : 0
    assignment.price =
      Number(baseDue) + customExtraForNonCardLike + donationChargeAmount
    if (donationChargeAmount > 0) {
      assignment.donationChargeAmount = donationChargeAmount
    }
    if (donationInvoiceFields.donations?.length) {
      assignment.donations = donationInvoiceFields.donations
    } else if (donationInvoiceFields.donation) {
      assignment.donation = donationInvoiceFields.donation
    }

    delete event?.page_views
    delete event?.tickets
    // Backend needs organization.uuid and/or connected_account_id to resolve destinationAccount; merge paymentOrg so those are always present.
    assignment['event'] = event
      ? {
          ...event,
          organization: paymentOrg
            ? { ...paymentOrg, ...event?.organization }
            : event?.organization,
        }
      : event
    assignment['eventPackage'] = eventPackage
    assignment['totalSeats'] = totalSeats
    assignment['unitTicketPrice'] =
      paymentMethod === 'COMP' || paymentMethod === 'RESERVED'
        ? 0
        : Number.parseFloat(price) || 0
    // in-person/online
    assignment['paymentOption'] = getPaymentOption(paymentMethod)
    assignment['paymentMethod'] = PAYMENT_TYPE[paymentMethod]
    assignment['paymentPlans'] = paymentPlans
    assignment['serviceFee'] = Number(effectivePricingObject?.serviceFee) || 0
    assignment['selectedSeats'] = currentSelectedSeatIds
    assignment['checkoutKey'] = assignmentCheckoutKeyRef.current
    // Top-level markers so payment service can resolve application_fee
    // even if eventPackage mode flags are stripped from the request body.
    if (isFlexPackAssignment && eventPackage?.uuid) {
      assignment.flexPackUUID = eventPackage.uuid
    }
    if (isAccessPassAssignment && eventPackage?.uuid) {
      assignment.accessPassTemplateUUID = eventPackage.uuid
    }

    if (isAssignCardLikePaymentMethod(paymentMethod)) {
      const pf = Number(effectivePricingObject?.paymentProcessingFee)
      if (Number.isFinite(pf) && pf > 0) {
        assignment.processingFee = pf
      }
      if (donationChargeAmount > 0) {
        assignment.processingFeeIncludesDonation = true
      }
    }

    const assignTicketsType =
      paymentMethod === 'COMP'
        ? 'comp'
        : paymentMethod === 'RESERVED'
          ? 'reserved'
          : ticketType
    const selectedBuyerOrComp = selectedBuyerType ?? selectedCompLabel
    const commonDraftData = {
      canReleaseTickets: true,
      organizationUUID: event
        ? event?.organizationUUID
        : eventPackage?.organizationUUID,
      paymentMethod: PAYMENT_TYPE[paymentMethod],
      paymentType: paymentSchedule,
      paymentOption: getPaymentOption(paymentMethod),
      assignTicketsType,
      selectedSeats: currentSelectedSeatIds,
      purchaseDetails: makePurchaseDetailsForDraft(
        currentSelectedSeatIds,
        eventPackage?.uuid
      ),
      assignTo,
      selectedBuyerType: selectedBuyerOrComp,
      fromOrderId: sourceOrderId,
      connectedAccountId,
      customFeeIds: customFeeIdsForInvoice,
      customFeeLines: customFeeLinesForInvoice,
      totalFeeAmount: customFeesApply ? totalCustomFeeAmount : 0,
      paymentIntentId: '',
      checkoutKey: assignmentCheckoutKeyRef.current,
      ...(seatHoldIdRef.current ? { seatHoldId: seatHoldIdRef.current } : {}),
      ...(pendingOrderIdRef.current
        ? { pendingOrderId: pendingOrderIdRef.current }
        : {}),
      ...(isComplimentaryStandaloneAssignment ? { complimentary: true } : {}),
      ...(isFlexPackAssignment ? { flexPackUUID: eventPackage?.uuid } : {}),
      ...(isAccessPassAssignment
        ? { accessPassTemplateUUID: eventPackage?.uuid }
        : {}),
      ...(isSupplementalCustomFee
        ? { supplementalForOrderId: addCustomFeeToOrderId }
        : {}),
    }
    if (hasPaymentPlan) {
      const fullTotal =
        (isAssignCardLikePaymentMethod(paymentMethod)
          ? safeNum(effectivePricingObject?.purchasePriceWithTaxAndFees)
          : safeNum(pricingObject?.purchasePriceWithTaxAndFees) +
            (customFeesApply ? totalCustomFeeAmount : 0)) + donationChargeAmount
      assignment.orderDraft = {
        endpoint: isSupplementalCustomFee
          ? 'create-supplemental-custom-fee-order'
          : isFlexPackAssignment
            ? 'create-flex-pack-assignment'
            : isAccessPassAssignment
              ? 'create-access-pass-assignment'
              : 'create-payment-plan-assignment',
        payload: {
          eventUUID: event?.uuid,
          packageUUID: eventPackage?.uuid,
          ...(isSupplementalCustomFee
            ? { sourceOrderId: addCustomFeeToOrderId }
            : {}),
          details: {
            ...commonDraftData,
            groupTicketsPrice: fullTotal,
            installments: Object.values(paymentPlans),
            hasPaymentPlan: true,
            currency: paymentOrg?.currency ?? organization?.currency ?? 'usd',
            pricing: buildDraftPricingSnapshot(
              isAssignCardLikePaymentMethod(paymentMethod),
              safeNum(amountDue)
            ),
            ...donationInvoiceFields,
          },
        },
      }
    } else {
      const assignmentDraftBody = {
        ...commonDraftData,
        dueDate: undefined,
        groupTicketsPrice: assignment.price,
        pricingTier: sourceOrderPricingTier || selectedPricingTier || undefined,
        pricing: buildDraftPricingSnapshot(
          isAssignCardLikePaymentMethod(paymentMethod),
          safeNum(assignment.price)
        ),
        ...donationInvoiceFields,
      }
      // Flex-pack / access-pass recovery reads `details`; ticket/package drafts use `invoice`.
      assignment.orderDraft = {
        endpoint: isSupplementalCustomFee
          ? 'create-supplemental-custom-fee-order'
          : isFlexPackAssignment
            ? 'create-flex-pack-assignment'
            : isAccessPassAssignment
              ? 'create-access-pass-assignment'
              : eventPackage && !sourceOrderId
                ? 'create-season-package-order'
                : 'create-order-assignment',
        payload: {
          eventUUID: event?.uuid,
          packageUUID: eventPackage?.uuid,
          ...(isSupplementalCustomFee
            ? { sourceOrderId: addCustomFeeToOrderId }
            : {}),
          ...(isFlexPackAssignment
            ? {
                details: {
                  ...assignmentDraftBody,
                  flexPackUUID: eventPackage?.uuid,
                },
              }
            : isAccessPassAssignment
              ? {
                  details: {
                    ...assignmentDraftBody,
                    accessPassTemplateUUID: eventPackage?.uuid,
                  },
                }
              : { invoice: assignmentDraftBody }),
        },
      }
    }

    return getPaymentIntent({ assignment })
      .then((res) => {
        const isDestinationCharge = Boolean(
          res?.data?.transfer_data?.destination
        )
        // Match Stripe.js context to where the PI was created (platform vs connected), including in demo/preview.
        const desiredStripeAccountContext = isDestinationCharge
          ? null
          : connectedAccountId
        const didContextChange =
          desiredStripeAccountContext !== stripeAccountContext

        if (typeof onStripeAccountContextChange === 'function') {
          onStripeAccountContextChange(desiredStripeAccountContext)
        }

        const secret = res?.data?.client_secret
        const pendingOrderId = res?.data?.metadata?.order_id
        if (pendingOrderId) pendingOrderIdRef.current = pendingOrderId
        paymentIntentIdRef.current =
          res?.data?.id || getPaymentIntentIdFromClientSecret(secret)

        if (didContextChange) {
          // Ensure Elements reinitializes with the new account context
          // before using this client secret for confirmCardPayment.
          setClientSecret('')
          setTimeout(() => {
            setClientSecret(secret)
          }, 50)
        } else {
          setClientSecret(secret)
        }
        lastPaymentIntentKeyRef.current = requestKey
        return secret
      })
      .catch((err) => {
        console.error(err)
        throw err
      })
      .finally(() => {
        if (inFlightPaymentIntentKeyRef.current === requestKey) {
          inFlightPaymentIntentKeyRef.current = null
        }
      })
  }

  const ensurePaymentIntentReady = async () => {
    if (clientSecret) {
      if (!paymentIntentIdRef.current) {
        paymentIntentIdRef.current =
          getPaymentIntentIdFromClientSecret(clientSecret)
      }
      return clientSecret
    }

    if (!shouldCreatePaymentIntent || !paymentIntentKey) {
      throw new Error(
        'Unable to prepare terminal payment. Check price and payment options.'
      )
    }

    inFlightPaymentIntentKeyRef.current = null
    const secret = await loadPaymentIntent(paymentIntentKey, { force: true })
    if (!secret) {
      throw new Error('Payment intent could not be created. Please try again.')
    }
    return secret
  }

  /* ────────────────────────────────────────────── */
  /*                Helper functions                */
  /* ────────────────────────────────────────────── */

  const shouldSkipPaymentIntent = useCallback(() => {
    if (paymentMethod === 'POS') return false

    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    const ticketZero = Number(parseFloat(price)) === 0
    const noCustomFees = !customFeesApply || totalCustomFeeAmount === 0
    // Any in-person method
    // if price is 0 and no custom fees
    if (
      (getPaymentOption(paymentMethod) === 'in-person' && !hasPaymentPlan) ||
      (ticketZero && noCustomFees)
    ) {
      return true
    }

    return false
  }, [
    paymentMethod,
    paymentPlans,
    price,
    customFeesApply,
    totalCustomFeeAmount,
  ])

  const isCreditCardOnlineOneTime = useCallback(() => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    return (
      paymentSchedule === 'one-time' &&
      getPaymentOption(paymentMethod) === 'online' &&
      paymentMethod === 'CARD' &&
      !hasPaymentPlan
    )
  }, [paymentMethod, paymentPlans, paymentSchedule])

  const isCreditCardOnlinePaymentPlan = useCallback(() => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    return (
      paymentMethod === 'CARD' &&
      getPaymentOption(paymentMethod) === 'online' &&
      paymentSchedule === 'payment-plan' &&
      hasPaymentPlan
    )
  }, [paymentMethod, paymentPlans, paymentSchedule])

  const isPosTerminalCase = useCallback(() => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    return (
      paymentMethod === 'POS' &&
      (paymentSchedule === 'one-time' ||
        (paymentSchedule === 'payment-plan' && hasPaymentPlan))
    )
  }, [paymentMethod, paymentPlans, paymentSchedule])

  const shouldCreatePaymentIntent = useMemo(() => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    const numericPrice = Number(price)
    const hasValidPrice =
      (Number.isFinite(numericPrice) && numericPrice > 0 && Boolean(price)) ||
      (customFeesApply && totalCustomFeeAmount > 0)
    const hasValidPlan =
      paymentSchedule === 'one-time'
        ? true
        : paymentSchedule === 'payment-plan'
          ? hasPaymentPlan
          : false

    if (
      !hasValidPrice ||
      !hasValidPlan ||
      shouldSkipPaymentIntent() ||
      isSavedCardOneTimeSelection
    ) {
      return false
    }

    return (
      isCreditCardOnlineOneTime() ||
      isCreditCardOnlinePaymentPlan() ||
      isPosTerminalCase()
    )
  }, [
    paymentMethod,
    paymentSchedule,
    paymentPlans,
    price,
    shouldSkipPaymentIntent,
    isCreditCardOnlineOneTime,
    isCreditCardOnlinePaymentPlan,
    isPosTerminalCase,
    isSavedCardOneTimeSelection,
    customFeesApply,
    totalCustomFeeAmount,
  ])

  const paymentIntentKey = useMemo(() => {
    if (!shouldCreatePaymentIntent) return null
    const plansKey = Object.values(paymentPlans || {})
      .map((plan) => `${plan?.id}:${plan?.amount}`)
      .join('|')
    return [
      paymentMethod,
      paymentSchedule,
      price,
      amountDue,
      plansKey,
      totalSeats,
      donationChargeAmount,
      donationExternalPending,
      selectedDonationRuleId,
      // Ensure PI regenerates when flex-pack / fee override changes service fee
      // (amountDue alone is not enough if another fee shifts compensate).
      Number(effectivePricingObject?.serviceFee) || 0,
      Number(effectivePricingObject?.paymentProcessingFee) || 0,
    ].join(':')
  }, [
    shouldCreatePaymentIntent,
    paymentMethod,
    paymentSchedule,
    price,
    amountDue,
    paymentPlans,
    totalSeats,
    donationChargeAmount,
    donationExternalPending,
    selectedDonationRuleId,
    effectivePricingObject?.serviceFee,
    effectivePricingObject?.paymentProcessingFee,
  ])

  useEffect(() => {
    if (!shouldCreatePaymentIntent) {
      lastPaymentIntentKeyRef.current = null
      inFlightPaymentIntentKeyRef.current = null
      return
    }

    if (!paymentIntentKey) return
    if (lastPaymentIntentKeyRef.current === paymentIntentKey) return

    loadPaymentIntent(paymentIntentKey)
  }, [shouldCreatePaymentIntent, paymentIntentKey])

  useEffect(() => {
    if (paymentMethod !== 'POS') {
      setReader(null)
      setStripeTerminal(undefined)
      terminalInstanceRef.current = null
    }
  }, [paymentMethod])

  const classifyAssignmentBuyerTypes = (types, defaults) => {
    const compKeys = compParentKeysFromDefaults(defaults)
    const assignmentTypes = (types ?? []).filter(
      buyerTypeHasTicketAssignmentSubType
    )
    return {
      comps: assignmentTypes.filter((type) =>
        isComplimentaryBuyerType(type, compKeys)
      ),
      paid: assignmentTypes.filter(
        (type) => !isComplimentaryBuyerType(type, compKeys)
      ),
    }
  }

  const loadCompLabels = () => {
    Promise.all([
      getBuyerTypes(organizationUUIDForAssignment),
      getSystemDefaultBuyerTypes(),
    ])
      .then(([typesRes, defaultsRes]) => {
        const { comps } = classifyAssignmentBuyerTypes(
          typesRes?.data?.data,
          defaultsRes?.data?.data
        )
        setCompLabels(comps)
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false))
  }

  const loadBuyerTypes = () => {
    Promise.all([
      getBuyerTypes(organizationUUIDForAssignment),
      getSystemDefaultBuyerTypes(),
    ])
      .then(([typesRes, defaultsRes]) => {
        const { paid } = classifyAssignmentBuyerTypes(
          typesRes?.data?.data,
          defaultsRes?.data?.data
        )
        setBuyerTypes(paid)
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false))
  }

  const loadExistingUsers = (query) => {
    return pastPurchasers(organizationUUIDForAssignment, query)
      .then((res) => {
        return res.data?.map((user) => ({
          value: user.email,
          label: capitalizeString(`${user.firstName} ${user.lastName}`),
          email: user.email,
          firstName: user?.firstName,
          lastName: user?.lastName,
          phoneNumber: user?.phoneNumber,
          custom: user?.email,
        }))
      })
      .catch((err) => {
        console.error(err)
        throw err
      })
  }

  // get selected tickets
  const idsToTicketLike = (selectedSeatIds, data) => {
    const tickets = []

    // Handle GA seats
    if (selectedSeatIds.gaSeats) {
      Object.entries(selectedSeatIds.gaSeats).forEach(
        ([sectionId, seatIds]) => {
          const section = data.sections[sectionId]
          if (section) {
            // Create a ticket for each GA seat in this section
            seatIds.forEach((seatId) => {
              tickets.push({
                generalAdmission: true,
                sectionNumber: section.sectionNumber,
                seatId: seatId,
              })
            })
          }
        }
      )
    }

    // Handle assigned seats
    if (selectedSeatIds.seats) {
      Object.keys(selectedSeatIds.seats).forEach((seatId) => {
        const seat = data.seats[seatId]
        if (seat) {
          tickets.push({
            generalAdmission: false,
            sectionNumber: seat.sectionNumber,
            rowNumber: seat.rowNumber,
            seatNumber: seat.seatNumber,
            seatId: seatId,
          })
        }
      })
    }

    return tickets
  }

  const ticketLikeArr = idsToTicketLike(
    currentSelectedSeatIds || {},
    currentData || { seats: {}, sections: {} }
  )
  const formattedGroups = formatTicketGroups(ticketLikeArr)
  const selectedSectionNumbers = useMemo(() => {
    const tickets = idsToTicketLike(
      currentSelectedSeatIds || {},
      currentData || { seats: {}, sections: {} }
    )
    return [
      ...new Set(
        tickets
          .map((ticket) => String(ticket.sectionNumber || '').trim())
          .filter(Boolean)
      ),
    ]
  }, [currentSelectedSeatIds, currentData])

  useEffect(() => {
    if (!eventPackage || step < 2 || !organizationUUIDForAssignment) {
      setDonationEvaluation(null)
      setSelectedDonationRuleId('')
      setConfiguredDonations([])
      setDonationAmount(0)
      setDonationExternalPending(false)
      return
    }

    const fanEmail = String(assignTo?.email || existingUser?.email || '').trim()
    if (!fanEmail || !selectedSectionNumbers.length) {
      setDonationEvaluation(null)
      return
    }

    let cancelled = false
    evaluateDonationAssignment({
      organizationUUID: organizationUUIDForAssignment,
      packageUUID: eventPackage.uuid,
      fanEmail,
      selectedSections: selectedSectionNumbers,
    })
      .then((res) => {
        if (cancelled) return
        const evaluation = res?.data || null
        setDonationEvaluation(evaluation)
        setConfiguredDonations([])

        const unmet = evaluation?.unmetRules || []
        if (unmet.length === 1) {
          setSelectedDonationRuleId(String(unmet[0].id))
        } else {
          setSelectedDonationRuleId('')
        }
      })
      .catch((error) => {
        console.error('Failed to evaluate donation assignment', error)
        if (!cancelled) setDonationEvaluation(null)
      })

    return () => {
      cancelled = true
    }
  }, [
    eventPackage,
    step,
    organizationUUIDForAssignment,
    assignTo?.email,
    existingUser?.email,
    selectedSectionNumbers,
  ])

  const openViewDonationProfile = useCallback(() => {
    const fanEmail = String(assignTo?.email || existingUser?.email || '').trim()
    if (!fanEmail) return

    openModal(
      () => (
        <PrintTicketsProvider>
          <ViewProfile
            order={{
              email: fanEmail,
              organizationUUID: organizationUUIDForAssignment,
            }}
            initialTab="donations"
          />
        </PrintTicketsProvider>
      ),
      {},
      {
        className: 'vjx-modal-2xl',
        contentClassName: 'h-lg-fixed',
        scrollable: true,
      }
    )
  }, [
    assignTo?.email,
    existingUser?.email,
    organizationUUIDForAssignment,
    openModal,
  ])

  const handleAssignToChange = (field, value) => {
    setAssignTo((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const cleanupAbandonedTerminalPayment = async () => {
    const paymentIntentId = paymentIntentIdRef.current
    if (!paymentIntentId) return

    try {
      await cancelTerminalPaymentIntent({
        paymentIntentId,
        connectedAccountId,
        destinationChargesEnabled,
      })
    } catch (error) {
      console.warn('Failed to cancel abandoned terminal payment intent', error)
    } finally {
      paymentIntentIdRef.current = null
      lastPaymentIntentKeyRef.current = null
      setClientSecret('')
    }
  }

  const cancelTerminalPayment = async () => {
    const terminal = terminalInstanceRef.current
    if (!terminal || !isCollectingPaymentRef.current) return

    try {
      await terminal.cancelCollectPaymentMethod()
    } catch (error) {
      console.warn('Failed to cancel terminal payment collection', error)
    }

    await cleanupAbandonedTerminalPayment()
  }

  const openPosWaitingModal = () =>
    openModal(
      TerminalPaymentWaiting,
      {
        statusLabel:
          TERMINAL_PAYMENT_STATUS_LABELS[terminalPaymentStatus] || undefined,
        onCancel: cancelTerminalPayment,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )

  const makePurchaseDetails = (selectedSeatIds, packageUUID) => {
    let purchaseDetails = {}
    if (!packageUUID) return purchaseDetails
    if (selectedSeatIds.gaSeats) {
      const targetSectionId = Object.keys(selectedSeatIds.gaSeats)[0]
      const targetSeatIds = (
        selectedSeatIds.gaSeats[targetSectionId] || []
      ).filter(Boolean)
      const desiredTicketCount = targetSeatIds.length
      purchaseDetails = {
        GA: true,
        sectionId: targetSectionId,
        desiredTicketCount,
        // Prefer the concrete GA seats chosen on the map so reservation stays
        // aligned with package_tickets / event tickets for those seat_ids.
        ...(targetSeatIds.length ? { targetSeatIds } : {}),
      }
    } else if (selectedSeatIds.seats) {
      purchaseDetails = {
        GA: false,
        targetSeatIds: Object.keys(selectedSeatIds.seats),
      }
    }
    return purchaseDetails
  }

  const buildPricingSnapshot = ({ fullTotal, dueNow }) => {
    const safeNum = (v) => {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }

    return {
      total: safeNum(fullTotal),
      salesTax: safeNum(pricingObject?.salesTax),
      serviceFee: safeNum(pricingObject?.serviceFee),
      processingFee: safeNum(effectivePricingObject?.paymentProcessingFee),
      processingFeeIncludesDonation: donationChargeAmount > 0,
      facilityFee: safeNum(pricingObject?.ticketFacilityFee),
      chargeBackProtection: safeNum(pricingObject?.chargeBackProtection),
      net: safeNum(pricingObject?.yourPayout),
      ticketCount: safeNum(totalSeats),
      amountDueNow: safeNum(dueNow),
    }
  }

  const createAssignment = async (paymentIntentId, dueDate) => {
    if (existingUser) assignTo['existingUser'] = existingUser.email

    const hasPaymentPlan = Object.keys(paymentPlans).length > 0
    const isEventReservedAssignment =
      paymentMethod === 'RESERVED' && Boolean(event?.uuid) && !eventPackage
    const sourceOrderAmount = Number(sourceOrderTotal)
    const hasSourceOrderAmount =
      Number.isFinite(sourceOrderAmount) && sourceOrderAmount > 0
    const baseGroupTickets = isEventReservedAssignment
      ? hasSourceOrderAmount
        ? sourceOrderAmount
        : getAmountDue(pricingObject, 'CASH', false, {})
      : getAmountDue(
          effectivePricingObject,
          paymentMethod,
          hasPaymentPlan,
          paymentPlans
        )
    const feeExtra =
      customFeesApply && !isAssignCardLikePaymentMethod(paymentMethod)
        ? totalCustomFeeAmount
        : 0
    const groupTicketsPrice = baseGroupTickets + feeExtra + donationChargeAmount

    const snapshotTotal =
      (isAssignCardLikePaymentMethod(paymentMethod)
        ? Number(effectivePricingObject?.purchasePriceWithTaxAndFees || 0)
        : Number(pricingObject?.purchasePriceWithTaxAndFees || 0) +
          (customFeesApply ? totalCustomFeeAmount : 0)) + donationChargeAmount

    const invoicePayload = {
      canReleaseTickets: true,
      organizationUUID: event
        ? event?.organizationUUID
        : eventPackage?.organizationUUID,
      paymentMethod: PAYMENT_TYPE[paymentMethod],
      paymentType: paymentSchedule,
      paymentOption: getPaymentOption(paymentMethod),
      assignTicketsType:
        paymentMethod === 'COMP'
          ? 'comp'
          : paymentMethod === 'RESERVED'
            ? 'reserved'
            : ticketType,
      groupTicketsPrice,
      customFeeIds: customFeeIdsForInvoice,
      customFeeLines: customFeeLinesForInvoice,
      totalFeeAmount: customFeesApply ? totalCustomFeeAmount : 0,
      selectedSeats: currentSelectedSeatIds,
      purchaseDetails: makePurchaseDetails(
        currentSelectedSeatIds,
        eventPackage?.uuid
      ),
      assignTo,
      selectedBuyerType: selectedBuyerType ?? selectedCompLabel,
      paymentIntentId,
      fromOrderId: sourceOrderId,
      connectedAccountId,
      dueDate,
      ...(seatHoldIdRef.current ? { seatHoldId: seatHoldIdRef.current } : {}),
      ...(pendingOrderIdRef.current
        ? { pendingOrderId: pendingOrderIdRef.current }
        : {}),
      pricing: buildPricingSnapshot({
        fullTotal: snapshotTotal,
        dueNow: groupTicketsPrice,
      }),
      pricingTier: sourceOrderPricingTier || selectedPricingTier || undefined,
      ...(isComplimentaryStandaloneAssignment ? { complimentary: true } : {}),
      ...donationInvoiceFields,
    }

    if (isFlexPackAssignment) {
      const requiresCardPayment =
        isAssignCardLikePaymentMethod(paymentMethod) &&
        paymentMethod !== 'INVOICE' &&
        Number(groupTicketsPrice) > 0 &&
        !isComplimentaryFlexPackAssignment
      if (requiresCardPayment && !String(paymentIntentId || '').trim()) {
        throw new Error(
          'A successful card payment is required before assigning this flex pack.'
        )
      }
      return await createFlexPackAssignment({
        details: {
          ...invoicePayload,
          flexPackUUID: eventPackage?.uuid,
          hasPaymentPlan: false,
        },
      })
    }

    if (isAccessPassAssignment) {
      const requiresCardPayment =
        isAssignCardLikePaymentMethod(paymentMethod) &&
        paymentMethod !== 'INVOICE' &&
        Number(groupTicketsPrice) > 0 &&
        !isComplimentaryAccessPassAssignment
      if (requiresCardPayment && !String(paymentIntentId || '').trim()) {
        throw new Error(
          'A successful card payment is required before assigning this access pass.'
        )
      }
      return await createAccessPassAssignment({
        details: {
          ...invoicePayload,
          accessPassTemplateUUID: eventPackage?.uuid,
          hasPaymentPlan: false,
        },
      })
    }

    const data = {
      eventUUID: event?.uuid,
      packageUUID: eventPackage?.uuid,
      invoice: invoicePayload,
    }

    if (isSupplementalCustomFee && eventPackage) {
      return await createSupplementalCustomFeeOrder({
        sourceOrderId: addCustomFeeToOrderId,
        invoice: {
          ...invoicePayload,
          supplementalForOrderId: addCustomFeeToOrderId,
        },
      })
    }

    if (addSeatsToOrderId && eventPackage) {
      const estimatedProcessingFee = isAssignCardLikePaymentMethod(
        paymentMethod
      )
        ? Number(effectivePricingObject?.paymentProcessingFee || 0)
        : 0
      const serviceFee = Number(pricingObject?.serviceFee || 0)
      const totalFeeAmount = customFeesApply ? totalCustomFeeAmount : 0
      const customFeeIds = customFeesApply ? customFeeIdsForInvoice : []

      return await addSeasonPackageOrderSeats({
        orderId: addSeatsToOrderId,
        selectedSeats: currentSelectedSeatIds,
        paymentIntentId,
        paymentMethod: PAYMENT_TYPE[paymentMethod],
        price: groupTicketsPrice,
        estimatedProcessingFee,
        serviceFee,
        customFeeIds,
        totalFeeAmount,
      })
    }

    return eventPackage && !sourceOrderId
      ? await createSeasonPackageOrder(data)
      : await createOrderAssignment(data)
  }

  const createOrder = (paymentIntentId = '', callback, dueDate = undefined) => {
    return createAssignment(paymentIntentId, dueDate)
      .then((result) => {
        succeededSavedCardChargeRef.current = null
        // Seats are sold; the server owns them now.
        seatHoldConsumedRef.current = true
        seatHoldIdRef.current = null
        seatHoldSeatsKeyRef.current = null
        setIsSaving(false)
        if (callback) callback()
        return result
      })
      .catch(async (err) => {
        console.error('createOrder failed:', err)

        // Worker may have already materialized the order for this PI while we
        // lost the inventory race — treat that as success instead of inviting
        // a second charge.
        if (paymentIntentId) {
          try {
            const recovered =
              await waitForOrderByPaymentIntentId(paymentIntentId)
            if (recovered?.orderId || recovered?.id) {
              succeededSavedCardChargeRef.current = null
              seatHoldConsumedRef.current = true
              seatHoldIdRef.current = null
              seatHoldSeatsKeyRef.current = null
              setIsSaving(false)
              if (callback) callback()
              return recovered
            }
          } catch (recoverErr) {
            console.error('createOrder recovery lookup failed:', recoverErr)
          }
        }

        const serverMessage = String(
          err?.response?.data?.error?.message ||
            err?.response?.data?.message ||
            err?.message ||
            ''
        )
        const paymentWasReversed =
          /automatically refunded/i.test(serverMessage) ||
          /automatically canceled/i.test(serverMessage)

        // Refunded/canceled PIs must not be reused on retry.
        if (paymentWasReversed || !paymentIntentId) {
          succeededSavedCardChargeRef.current = null
        }

        // The server released the hold as part of reversing the payment.
        if (paymentWasReversed) {
          seatHoldIdRef.current = null
          seatHoldSeatsKeyRef.current = null
        }

        setIsSaving(false)

        let errorBody
        if (isSupplementalCustomFee) {
          errorBody = 'Could not add the custom fee to this account.'
        } else if (addSeatsToOrderId) {
          errorBody = 'Could not add seats to this order.'
        } else if (isFlexPackAssignment) {
          errorBody = 'Could not assign flex pack to user.'
        } else if (isAccessPassAssignment) {
          errorBody = 'Could not assign access pass to user.'
        } else if (paymentWasReversed) {
          errorBody = serverMessage
        } else if (paymentIntentId) {
          errorBody =
            serverMessage &&
            serverMessage !== 'Request failed with status code 500'
              ? serverMessage
              : 'Payment may have succeeded but assignment failed. Do not charge again — refresh and confirm the order, or contact support.'
        } else {
          errorBody = 'Could not assign tickets to user.'
        }

        openError(
          paymentWasReversed
            ? 'Assignment failed — payment reversed'
            : 'Something went wrong!',
          errorBody
        )
        throw err
      })
  }

  const createPaymentPlanAssignment = async (paymentIntentId) => {
    const updatedIntent = await updatePaymentIntent({
      assignTo,
      paymentIntentId,
      connectedAccountId,
    })

    if (updatedIntent?.status !== 200) {
      console.log('Payment did not fully succeed:', updatedIntent.status)
      return
    }

    if (existingUser) assignTo['existingUser'] = existingUser.email

    const fullTotal =
      (isAssignCardLikePaymentMethod(paymentMethod)
        ? Number(effectivePricingObject?.purchasePriceWithTaxAndFees || 0)
        : Number(pricingObject?.purchasePriceWithTaxAndFees || 0) +
          (customFeesApply ? totalCustomFeeAmount : 0)) + donationChargeAmount

    const detailsPayload = {
      canReleaseTickets: true,
      organizationUUID: event
        ? event?.organizationUUID
        : eventPackage?.organizationUUID,
      paymentMethod: PAYMENT_TYPE[paymentMethod],
      paymentType: paymentSchedule,
      paymentOption: getPaymentOption(paymentMethod),
      assignTicketsType:
        paymentMethod === 'COMP'
          ? 'comp'
          : paymentMethod === 'RESERVED'
            ? 'reserved'
            : ticketType,
      groupTicketsPrice: fullTotal,
      customFeeIds: customFeeIdsForInvoice,
      customFeeLines: customFeeLinesForInvoice,
      totalFeeAmount: customFeesApply ? totalCustomFeeAmount : 0,
      selectedSeats: currentSelectedSeatIds,
      purchaseDetails: makePurchaseDetails(
        currentSelectedSeatIds,
        eventPackage?.uuid
      ),
      assignTo,
      selectedBuyerType: selectedBuyerType ?? selectedCompLabel,
      paymentIntentId,
      fromOrderId: sourceOrderId,
      // The pending checkout row already holds these seats, so finalizing has
      // to claim them instead of reserving again from available inventory.
      ...(seatHoldIdRef.current ? { seatHoldId: seatHoldIdRef.current } : {}),
      ...(pendingOrderIdRef.current
        ? { pendingOrderId: pendingOrderIdRef.current }
        : {}),
      installments: Object.values(paymentPlans),
      hasPaymentPlan: true,
      connectedAccountId,
      currency: paymentOrg?.currency ?? organization?.currency ?? 'usd',
      pricing: buildPricingSnapshot({ fullTotal, dueNow: amountDue }),
      ...(isComplimentaryStandaloneAssignment ? { complimentary: true } : {}),
      ...donationInvoiceFields,
    }

    if (isFlexPackAssignment) {
      const requiresCardPayment =
        isAssignCardLikePaymentMethod(paymentMethod) &&
        paymentMethod !== 'INVOICE' &&
        Number(fullTotal) > 0 &&
        !isComplimentaryFlexPackAssignment
      if (requiresCardPayment && !String(paymentIntentId || '').trim()) {
        throw new Error(
          'A successful card payment is required before assigning this flex pack.'
        )
      }
      return await createFlexPackAssignment({
        details: {
          ...detailsPayload,
          flexPackUUID: eventPackage?.uuid,
        },
      })
    }

    if (isAccessPassAssignment) {
      const requiresCardPayment =
        isAssignCardLikePaymentMethod(paymentMethod) &&
        paymentMethod !== 'INVOICE' &&
        Number(fullTotal) > 0 &&
        !isComplimentaryAccessPassAssignment
      if (requiresCardPayment && !String(paymentIntentId || '').trim()) {
        throw new Error(
          'A successful card payment is required before assigning this access pass.'
        )
      }
      return await createAccessPassAssignment({
        details: {
          ...detailsPayload,
          accessPassTemplateUUID: eventPackage?.uuid,
        },
      })
    }

    return await createOrderPaymentPlan({
      eventUUID: event?.uuid,
      packageUUID: eventPackage?.uuid,
      details: detailsPayload,
    })
  }

  const createPaymentPlanOrder = (paymentIntentId = '', callback) => {
    createPaymentPlanAssignment(paymentIntentId)
      .then((res) => {
        console.log('create payment plan assignment then', res)
        setIsSaving(false)
        if (callback) callback()
      })
      .catch((err) => {
        console.log('errro', err)
        setIsSaving(false)
        if (!isSaving)
          openError(
            'Something went wrong!',
            isFlexPackAssignment
              ? 'Could not assign flex pack to user.'
              : isAccessPassAssignment
                ? 'Could not assign access pass to user.'
                : 'Could not assign tickets to user.'
          )
      })
  }

  const handleCardPayment = async () => {
    // process card payments
    if (getPaymentOption(paymentMethod) !== 'online') {
      // dont use if not card payment
      console.warn('Non card payment selected')
      return
    }

    setIsSaving(true)

    const email = assignTo.email

    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card,
          billing_details: { email },
        },
      }
    )

    if (error) {
      console.error(error.message, ' Payment failed.')
      setIsSaving(false)
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      createOrder(
        paymentIntent.id,
        addSeatsToOrderId ? showAssignmentSuccess : showPaymentSuccess
      )
    }
  }

  const showPaymentSuccess = () => {
    showSuccess('Payment received successfully!', 'Amount paid', amountDue)
  }

  const showSuccess = (title, description, amount) => {
    openSuccess(
      title,
      description,
      amount ? `${formatCurrency(amount)}` : undefined,
      event?.uuid,
      eventPackage?.uuid,
      isFlexPackAssignment,
      isAccessPassAssignment
    )
  }

  const showAssignmentSuccess = () => {
    if (isSupplementalCustomFee) {
      showSuccess('Custom fee added successfully', null, null)
      return
    }
    if (addSeatsToOrderId) {
      showSuccess('Seats added successfully', null, null)
      return
    }
    showSuccess('Ticket assigned successfully', null, null)
  }

  const onInvoiceSuccess = () => {
    showSuccess('Invoice sent successfully!', null, null)
  }

  const onSuccessClose = (redirectURL) => {
    closeModal()
    if (addSeatsToOrderId || isSupplementalCustomFee) {
      if (typeof reloadAssignments === 'function') {
        reloadAssignments(0)
      }
      return
    }
    navigate(redirectURL)
    if (typeof reloadAssignments === 'function') {
      reloadAssignments(0)
    }
  }

  const openSuccess = (
    title,
    description,
    highlightText,
    eventUUID,
    packageUUID,
    flexPackMode = false,
    accessPassMode = false
  ) => {
    // const description = `A copy of the invoice and receipt has been sent to ${email}`;
    const eventRedirectURL = `/event/${eventUUID}/assign-tickets`
    const packageRedirectURL = `/package/${packageUUID}/assign-packages`
    const flexPackRedirectURL = `/flex-package/${packageUUID}/flex-package-holders`
    const accessPassRedirectURL = `/access-passes`
    const redirectURL = accessPassMode
      ? accessPassRedirectURL
      : flexPackMode
        ? flexPackRedirectURL
        : eventUUID
          ? eventRedirectURL
          : packageRedirectURL
    openModal(
      Success,
      {
        title,
        description,
        highlightText,
        onClose: () => onSuccessClose(redirectURL),
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const openError = (title, description) => {
    openModal(
      Error,
      {
        title,
        description,
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const openMarkAsComplete = (paymentType) => {
    // MarkAsComplete replaces this form but still submits through createOrder, so
    // the hold has to outlive the unmount or the submit arrives with a released
    // hold and the server rejects it as expired.
    seatHoldHandedOffRef.current = true
    openModal(
      MarkAsComplete,
      {
        paymentType,
        amountToCollect: amountDue,
        createOrder,
        showAssignmentSuccess,
        onClose: () => closeModal(),
        onAbandon: releaseSeatHoldIfUnused,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const onDeleteClose = () => {
    setIsAnotherModalOpen(false)
    closeModal()
  }

  const openDelete = (id) => {
    setIsAnotherModalOpen(true)
    openModal(
      Delete,
      {
        entity: 'saved card',
        text: 'This card will no longer be saved for future purchases.',
        id,
        handleDelete: handleDeleteCard,
        onClose: () => onDeleteClose(),
      },
      { className: 'modal--lg', contentClassName: '' },
      { replace: false }
    )
  }

  const handleDeleteCard = (id) => {
    return new Promise((resolve, reject) => {
      setSavedCards(savedCards.filter((card) => card.id != id))
      resolve()
    })
  }

  const onRemoveSeat = (section, row, seat, ticketInfo = {}) => {
    setIsAnotherModalOpen(true)
    openModal(
      Confirm,
      {
        title: 'Remove seat?',
        description: `Are you sure you want to remove Sec ${section}, Row ${row}, Seat ${seat} from this order?`,
        onConfirm: () => {
          const explicitSeatId =
            ticketInfo?.seatId || ticketInfo?.seat_id || ticketInfo?.id
          const sectionNorm = String(section).trim()
          const rowNorm = String(row).trim()
          const seatNorm = String(seat).trim()
          const seatEntries = Object.entries(currentData?.seats || {})
          const seatIdToRemove =
            explicitSeatId ||
            seatEntries.find(([, seatInfo]) => {
              return (
                String(seatInfo?.sectionNumber).trim() === sectionNorm &&
                String(seatInfo?.rowNumber).trim() === rowNorm &&
                String(seatInfo?.seatNumber).trim() === seatNorm
              )
            })?.[0]

          if (seatIdToRemove) {
            setCurrentSelectedSeatIds((prev) => {
              const prevSeats = { ...(prev?.seats || {}) }
              delete prevSeats[seatIdToRemove]
              return { ...prev, seats: prevSeats }
            })

            setCurrentData((prev) => {
              const nextSeats = { ...(prev?.seats || {}) }
              delete nextSeats[seatIdToRemove]
              return { ...prev, seats: nextSeats }
            })
          }

          setIsAnotherModalOpen(false)
          closeModal()
        },
        onClose: () => {
          setIsAnotherModalOpen(false)
          closeModal()
        },
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )
  }

  const handleGoBack = () => {
    setStep((currentStep) => {
      const nextStep = Math.max(0, currentStep - 1)
      if (nextStep === 0) {
        setUserType('existing-user')
      }
      return nextStep
    })
  }

  // button text
  const getText = () => {
    if (isSupplementalCustomFee) return 'Add custom fee'
    if (addSeatsToOrderId) return 'Add seats'
    switch (paymentMethod) {
      case 'invoice':
        return 'Send invoice'
      case 'POS':
        return 'Continue to Terminal'
      case 'CARD':
        return 'Authorize payment'
      default:
        return 'Assign tickets'
    }
  }

  const ensureTerminalConnected = async () => {
    if (!terminalInfo?.location) {
      throw new Error('No POS terminal location configured for this profile')
    }

    let terminal = terminalInstanceRef.current

    if (!terminal) {
      const StripeTerminal = await loadStripeTerminal()

      if (!StripeTerminal) {
        throw new Error('Stripe Terminal JS SDK failed to load.')
      }

      terminal = StripeTerminal.create({
        onFetchConnectionToken: async () => {
          const response = await getConnectionToken()
          return response.data.secret
        },
        onUnexpectedReaderDisconnect: () => {
          console.warn('Reader disconnected unexpectedly')
          setReader(null)
        },
        onPaymentStatusChange: (status) => {
          setTerminalPaymentStatus(status)
        },
      })

      terminalInstanceRef.current = terminal
      setStripeTerminal(terminal)
    }

    if (terminal.getConnectedReader?.()) {
      return terminal
    }

    const discoverResult = await terminal.discoverReaders({
      simulated: process.env.REACT_APP_STAGE !== 'production',
      location: terminalInfo.location,
    })

    if (discoverResult.error) {
      throw discoverResult.error
    }

    const discoveredReaders = discoverResult.discoveredReaders || []
    const assignedReaderId = terminalInfo?.stripeReaderId
    const readerToConnect =
      (assignedReaderId &&
        discoveredReaders.find((item) => item.id === assignedReaderId)) ||
      discoveredReaders[0]

    if (!readerToConnect) {
      throw new Error(
        'No readers discovered for this terminal location. Make sure your reader is online and assigned to your account.'
      )
    }

    const connectResult = await terminal.connectReader(readerToConnect)

    if (connectResult.error) {
      throw connectResult.error
    }

    setReader(connectResult.reader)
    return terminal
  }

  const processTerminalPayment = async () => {
    terminalTimedOutRef.current = false
    setTerminalPaymentStatus('')
    openPosWaitingModal()

    try {
      const paymentClientSecret = await ensurePaymentIntentReady()
      const terminal = await ensureTerminalConnected()

      const handleTimeout = () => {
        terminalTimedOutRef.current = true
        cancelTerminalPayment()
      }

      return await runTerminalPaymentFlow({
        terminal,
        clientSecret: paymentClientSecret,
        isCollectingRef: isCollectingPaymentRef,
        collectTimeoutRef,
        onTimeout: handleTimeout,
      })
    } finally {
      closeModal()
    }
  }

  const addCustomFeeRow = () => {
    customFeeRowIdRef.current += 1
    setCustomFeeRows((rows) => [
      ...rows,
      { id: customFeeRowIdRef.current, feeId: null },
    ])
  }

  const removeCustomFeeRow = (rowId) => {
    setCustomFeeRows((rows) => rows.filter((r) => r.id !== rowId))
  }

  const setCustomFeeRowFeeId = (rowId, feeId) => {
    setCustomFeeRows((rows) =>
      rows.map((r) => (r.id === rowId ? { ...r, feeId } : r))
    )
  }

  const chargePaymentMethod = async () => {
    const data = {}
    data['assignTo'] = assignTo
    const feeExtra =
      customFeesApply && !isAssignCardLikePaymentMethod(paymentMethod)
        ? totalCustomFeeAmount
        : 0
    const baseCharge = isAssignCardLikePaymentMethod(paymentMethod)
      ? Number(effectivePricingObject?.purchasePriceWithTaxAndFees)
      : Number(pricingObject?.purchasePriceWithTaxAndFees)
    data['amount'] =
      (Number.isFinite(baseCharge) ? baseCharge : 0) +
      feeExtra +
      donationChargeAmount
    data['connectedAccountId'] = connectedAccountId
    data['paymentMethodId'] = selectedCard
    data['currency'] = paymentOrg?.currency ?? organization?.currency ?? 'usd'
    // Required for destination application_fee (service + processing), matching
    // getPaymentIntent / createSeasonPackagePaymentIntent for new-card flows.
    data['serviceFee'] = Number(effectivePricingObject?.serviceFee) || 0
    const processingFee = Number(effectivePricingObject?.paymentProcessingFee)
    if (Number.isFinite(processingFee) && processingFee > 0) {
      data['processingFee'] = processingFee
    }
    const savedCardDraft = {
      canReleaseTickets: true,
      organizationUUID: event
        ? event?.organizationUUID
        : eventPackage?.organizationUUID,
      paymentMethod: PAYMENT_TYPE[paymentMethod],
      paymentType: paymentSchedule,
      paymentOption: getPaymentOption(paymentMethod),
      assignTicketsType: ticketType,
      selectedSeats: currentSelectedSeatIds,
      purchaseDetails: makePurchaseDetails(
        currentSelectedSeatIds,
        eventPackage?.uuid
      ),
      assignTo,
      selectedBuyerType: selectedBuyerType ?? selectedCompLabel,
      fromOrderId: sourceOrderId,
      connectedAccountId,
      customFeeIds: customFeeIdsForInvoice,
      customFeeLines: customFeeLinesForInvoice,
      totalFeeAmount: customFeesApply ? totalCustomFeeAmount : 0,
      groupTicketsPrice: data.amount,
      checkoutKey: assignmentCheckoutKeyRef.current,
      pricing: buildPricingSnapshot({
        fullTotal: data.amount,
        dueNow: data.amount,
      }),
      pricingTier: sourceOrderPricingTier || selectedPricingTier || undefined,
      ...donationInvoiceFields,
      ...(seatHoldIdRef.current ? { seatHoldId: seatHoldIdRef.current } : {}),
      ...(pendingOrderIdRef.current
        ? { pendingOrderId: pendingOrderIdRef.current }
        : {}),
    }
    data['assignment'] = {
      assignTo,
      event,
      eventPackage,
      checkoutKey: assignmentCheckoutKeyRef.current,
      selectedSeats: currentSelectedSeatIds,
      totalSeats: currentSelectedSeatIds?.length || 1,
      price: data.amount,
      paymentMethod: 'card',
      paymentOption: 'online',
      orderDraft: {
        endpoint:
          eventPackage && !sourceOrderId
            ? isFlexPackAssignment
              ? 'create-flex-pack-assignment'
              : isAccessPassAssignment
                ? 'create-access-pass-assignment'
                : 'create-season-package-order'
            : 'create-order-assignment',
        payload: {
          eventUUID: event?.uuid,
          packageUUID: eventPackage?.uuid,
          ...(isFlexPackAssignment || isAccessPassAssignment
            ? { details: savedCardDraft }
            : { invoice: savedCardDraft }),
        },
      },
    }

    const chargeFingerprint = JSON.stringify({
      amount: data.amount,
      paymentMethodId: selectedCard,
      email: assignTo?.email,
      packageUUID: eventPackage?.uuid,
      eventUUID: event?.uuid,
      seats: currentSelectedSeatIds,
    })

    // If a prior submit already charged this exact assignment, reuse that PI
    // instead of creating another confirmed PaymentIntent.
    const prior = succeededSavedCardChargeRef.current
    if (prior?.paymentIntentId && prior?.fingerprint === chargeFingerprint) {
      return { data: { id: prior.paymentIntentId } }
    }

    try {
      const res = await changeExistingPaymentMethod(data)
      const paymentIntentId = res?.data?.id
      const pendingOrderId = res?.data?.metadata?.order_id
      if (pendingOrderId) pendingOrderIdRef.current = pendingOrderId
      if (paymentIntentId) {
        succeededSavedCardChargeRef.current = {
          paymentIntentId,
          fingerprint: chargeFingerprint,
        }
      }
      return res
    } catch (err) {
      console.error('chargePaymentMethod: Error ', err)
    }
  }

  // Creating a new order
  return (
    <Modal.Body
      className={`d-flex-column ${isAnotherModalOpen ? 'modal-opac' : ''}`}
    >
      {/* to load comps and buyer types */}
      {isLoading && step === 2 ? (
        <LoadingContainer />
      ) : (
        <ModalFormV2
          selectedTickets={formattedGroups}
          isPackage={Boolean(eventPackage)}
          ticketType={ticketType}
          price={price}
          setPrice={setPrice}
          priceTiers={priceTiers}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          paymentSchedule={paymentSchedule}
          setPaymentSchedule={setPaymentSchedule}
          pricingObject={effectivePricingObject}
          isComplimentary={isComplimentary}
          assignTo={assignTo}
          onAssignToChange={handleAssignToChange}
          isValidEmail={isValidEmail}
          setIsValidEmail={setIsValidEmail}
          isValidNumber={isValidNumber}
          setIsValidNumber={setIsValidNumber}
          buyerTypes={buyerTypes}
          selectedBuyerType={selectedBuyerType}
          compLabels={compLabels}
          selectedCompLabel={selectedCompLabel}
          setSelectedBuyerType={setSelectedBuyerType}
          setSelectedCompLabel={setSelectedCompLabel}
          clientSecret={clientSecret}
          initialClientSecret={initialClientSecret}
          setCard={setCard}
          setStripe={setStripe}
          dueDateOpt={dueDateOpt}
          setDueDateOpt={setDueDateOpt}
          customDueDate={customDueDate}
          setCustomDueDate={setCustomDueDate}
          userType={userType}
          setUserType={setUserType}
          initialExistingUser={initialExistingUserState}
          setInitialExistingUser={setInitialExistingUser}
          existingUser={existingUser}
          setExistingUser={setExistingUser}
          installmentMonths={installmentMonths}
          setInstallmentMonths={setInstallmentMonths}
          paymentPlans={paymentPlans}
          setPaymentDate={setPaymentDate}
          loadExistingUsers={loadExistingUsers}
          setIsCardValid={setIsCardValid}
          savedCards={savedCards}
          isLoadingCards={isLoadingCards}
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
          selectedCardOpt={selectedCardOpt}
          setSelectedCardOpt={setSelectedCardOpt}
          step={step}
          setStep={setStep}
          alert={alert}
          closeModal={closeModal}
          isValid={isValid}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          getText={getText}
          handleAssignToChange={handleAssignToChange}
          handleGoBack={handleGoBack}
          createOrder={createOrder}
          card={card}
          openError={openError}
          showSuccess={showSuccess}
          showPaymentSuccess={showPaymentSuccess}
          openMarkAsComplete={openMarkAsComplete}
          openDelete={openDelete}
          processTerminalPayment={processTerminalPayment}
          terminalPaymentTimedOutRef={terminalTimedOutRef}
          createPaymentPlanOrder={createPaymentPlanOrder}
          chargePaymentMethod={chargePaymentMethod}
          ensureSeatHold={ensureSeatHold}
          eventPackage={eventPackage}
          onInvoiceSuccess={onInvoiceSuccess}
          showAssignmentSuccess={showAssignmentSuccess}
          showRemoveSeat={Boolean(sourceOrderId) && !addSeatsToOrderId}
          supplementalFeeOnly={isSupplementalCustomFee}
          sourceOrderTotal={sourceOrderTotal}
          onRemoveSeat={onRemoveSeat}
          accessPassQuantity={accessPassQuantity}
          onAccessPassQuantityChange={
            isAccessPassAssignment ? applyAccessPassQuantity : undefined
          }
          customFeesList={customFeesList}
          customFeeRows={customFeeRows}
          addCustomFeeRow={addCustomFeeRow}
          removeCustomFeeRow={removeCustomFeeRow}
          setCustomFeeRowFeeId={setCustomFeeRowFeeId}
          totalCustomFeeAmount={totalCustomFeeAmount}
          customFeeSummaryLines={customFeeSummaryLines}
          organizationUUIDForAssignment={organizationUUIDForAssignment}
          donationEvaluation={donationEvaluation}
          selectedDonationRuleId={selectedDonationRuleId}
          setSelectedDonationRuleId={setSelectedDonationRuleId}
          configuredDonations={configuredDonations}
          onApplyDonation={handleApplyDonation}
          donationAmount={donationAmount}
          setDonationAmount={setDonationAmount}
          donationExternalPending={donationExternalPending}
          setDonationExternalPending={setDonationExternalPending}
          onViewDonation={openViewDonationProfile}
          donationChargeAmount={donationChargeAmount}
        />
      )}
    </Modal.Body>
  )
}
